import React, { useState } from 'react';
import { Shield, TrendingUp, Users, Award, Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster } from '../components/ui/sonner';
import QuoteForm from '../components/QuoteForm';
import ContactForm from '../components/ContactForm';

const Home = () => {
  const whatsappNumber = "573115318547"; // Official ProtecCapital WhatsApp
  const whatsappMessage = encodeURIComponent("Hola, me interesa obtener información sobre los servicios de ProtecCapital.");
  // Using direct WhatsApp link format that works better across devices
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const seguros = [
    { name: "Seguros de Vida", icon: "👤" },
    { name: "Seguros de Hogar", icon: "🏠" },
    { name: "Seguros de Arrendamiento", icon: "🔑" },
    { name: "Seguros de Mascotas", icon: "🐾" },
    { name: "Seguros de Autos", icon: "🚗" },
    { name: "Seguros de Cumplimiento", icon: "✅" },
    { name: "SOAT", icon: "🛡️" },
    { name: "Y muchos más...", icon: "➕" }
  ];

  const creditos = [
    { name: "Créditos Vehiculares", description: "Financiamiento para la compra de tu vehículo" },
    { name: "Créditos Hipotecarios", description: "Cumple el sueño de tu casa propia" },
    { name: "Créditos Libre Inversión", description: "Con garantía real para tus proyectos" }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-sm z-50 transition-all duration-300">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_72236df9-a69d-4a4c-b1c1-73a1282c9727/artifacts/g2s2jmkq_ChatGPT%20Image%2021%20feb%202026%2C%2007_55_23%20p.m..png" 
                alt="ProtecCapital Logo" 
                className="h-12 w-auto"
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('inicio')} className="text-[#0d1b2a] hover:text-[#1565c0] font-medium transition-colors">
                Inicio
              </button>
              <button onClick={() => scrollToSection('servicios')} className="text-[#0d1b2a] hover:text-[#1565c0] font-medium transition-colors">
                Servicios
              </button>
              <button onClick={() => scrollToSection('nosotros')} className="text-[#0d1b2a] hover:text-[#1565c0] font-medium transition-colors">
                Nosotros
              </button>
              <button onClick={() => scrollToSection('contacto')} className="text-[#0d1b2a] hover:text-[#1565c0] font-medium transition-colors">
                Contacto
              </button>
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-gradient-to-r from-[#1565c0] to-[#00c853] hover:from-[#1e88e5] hover:to-[#43a047] text-white px-6 py-2 rounded-md transition-all duration-300 font-medium"
              >
                <Phone className="w-4 h-4 mr-2" />
                WhatsApp
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1565c0]/5 via-transparent to-[#00c853]/5"></div>
        <div className="container mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl font-bold text-[#0d1b2a] leading-tight">
                Protegemos tu <span className="text-[#1565c0]">Capital</span>,
                <br />Impulsamos tu <span className="text-[#00c853]">Futuro</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Seguros y créditos con garantía real. Soluciones financieras confiables para proteger lo que más valoras y hacer crecer tu patrimonio.
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-gradient-to-r from-[#1565c0] to-[#00c853] hover:from-[#1e88e5] hover:to-[#43a047] text-white px-8 py-4 text-lg transition-all duration-300 shadow-lg hover:shadow-xl rounded-md font-medium"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Contáctanos por WhatsApp
                </a>
                <Button 
                  onClick={() => scrollToSection('servicios')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#1565c0] text-[#1565c0] hover:bg-[#1565c0] hover:text-white px-8 py-6 text-lg transition-all duration-300"
                >
                  Ver Servicios
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <a 
                  href="https://www.instagram.com/proteccapital/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-600 hover:text-[#1565c0] transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                  <span className="text-sm font-medium">@proteccapital</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/u8ufcvun_image.png" 
                alt="Familia Feliz Protegida con ProtecCapital" 
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-center"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1565c0] to-[#00c853] rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0d1b2a]">+1000</p>
                    <p className="text-sm text-gray-600">Clientes Satisfechos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
              <div className="h-52 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/5082949/pexels-photo-5082949.jpeg" 
                  alt="Familia Protegida" 
                  className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-105"
                />
              </div>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1565c0] to-[#1e88e5] rounded-2xl flex items-center justify-center mx-auto mb-4 -mt-12 relative z-10 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0d1b2a] mb-3">Seguridad Garantizada</h3>
                <p className="text-gray-600">
                  Protegemos tu patrimonio y el de tu familia con las mejores aseguradoras
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
              <div className="h-52 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1664548324482-108e52115a70" 
                  alt="Crecimiento Familiar" 
                  className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-105"
                />
              </div>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#00c853] to-[#43a047] rounded-2xl flex items-center justify-center mx-auto mb-4 -mt-12 relative z-10 shadow-lg">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0d1b2a] mb-3">Crecimiento Financiero</h3>
                <p className="text-gray-600">
                  Créditos con garantía real para cumplir los sueños de tu familia
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
              <div className="h-52 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/8441820/pexels-photo-8441820.jpeg" 
                  alt="Asesoría Cercana" 
                  className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-105"
                />
              </div>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1565c0] to-[#00c853] rounded-2xl flex items-center justify-center mx-auto mb-4 -mt-12 relative z-10 shadow-lg">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0d1b2a] mb-3">Asesoría Profesional</h3>
                <p className="text-gray-600">
                  Expertos comprometidos que entienden tus necesidades
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partners/Aliados Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0d1b2a] mb-4">
              Nuestros Aliados Estratégicos
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1565c0] to-[#00c853] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trabajamos con las aseguradoras más confiables de Colombia para ofrecerte la mejor protección
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {/* Seguros del Estado */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/oivz9rio_image.png"
                alt="Seguros del Estado"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Aseguradora Solidaria */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/cupho04g_image.png"
                alt="Aseguradora Solidaria"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Seguros Mundial */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/dcnitvnv_image.png"
                alt="Seguros Mundial"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* SBS Seguros */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/w5s0oli0_image.png"
                alt="SBS Seguros"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Seguros Bolívar */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/2ki6ls5n_image.png"
                alt="Seguros Bolívar"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Zurich */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/8khulzzf_image.png"
                alt="Zurich Seguros"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* HDI Seguros */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_1a94da4a-1ecb-4893-aa6d-d3e6014b6dff/artifacts/57fzy1pc_image.png"
                alt="HDI Seguros"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Qualitas */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/z56s3xmg_image.png"
                alt="Qualitas Seguros"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Equidad Seguros */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/0xv2ydkm_image.png"
                alt="Equidad Seguros"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Liberty Seguros */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/6a7685pd_image.png"
                alt="Liberty Seguros"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Sura */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_protec-capital/artifacts/9679ra7s_image.png"
                alt="Sura Seguros"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          <div className="text-center mt-12">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#1565c0] to-[#00c853] text-white px-8 py-4 rounded-full shadow-lg">
              <Shield className="w-6 h-6" />
              <p className="font-bold text-lg">+11 Aseguradoras de Confianza</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0d1b2a] mb-4">
              Nuestros Servicios
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Soluciones integrales en seguros y créditos para proteger y hacer crecer tu capital
            </p>
          </div>

          {/* Seguros */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1565c0] to-[#1e88e5] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-[#0d1b2a]">Seguros</h3>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {seguros.map((seguro, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[#1565c0]">
                  <CardContent className="p-6 text-center">
                    <div className="text-4xl mb-3">{seguro.icon}</div>
                    <p className="font-semibold text-[#0d1b2a] group-hover:text-[#1565c0] transition-colors">
                      {seguro.name}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Créditos */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00c853] to-[#43a047] rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-[#0d1b2a]">Créditos con Garantía Real</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {creditos.map((credito, index) => (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-none bg-gradient-to-br from-gray-50 to-white">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#00c853] to-[#43a047] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <TrendingUp className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-[#0d1b2a] mb-2">{credito.name}</h4>
                    <p className="text-gray-600">{credito.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <a 
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-gradient-to-r from-[#1565c0] to-[#00c853] hover:from-[#1e88e5] hover:to-[#43a047] text-white px-10 py-4 text-lg transition-all duration-300 shadow-lg hover:shadow-xl rounded-md font-medium"
            >
              Solicita tu Cotización Gratis
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-20 px-4 bg-gradient-to-br from-[#1565c0]/5 via-white to-[#00c853]/5">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab" 
                alt="Oficinas ProtecCapital" 
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-center"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0d1b2a]">
                Sobre <span className="text-[#1565c0]">Nosotros</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                <strong className="text-[#0d1b2a]">ProtecCapital</strong> nace como un emprendimiento con visión de futuro, comprometido en ofrecer soluciones financieras integrales en toda Colombia.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Con sede en Cali, Colombia, brindamos servicios de seguros y colocación de créditos con garantía real a nivel nacional, construyendo relaciones de confianza con nuestros clientes.
              </p>
              <div className="grid grid-cols-2 gap-6 pt-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1565c0] to-[#00c853] rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#0d1b2a]">+1000</p>
                      <p className="text-sm text-gray-600">Clientes</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1565c0] to-[#00c853] rounded-lg flex items-center justify-center">
                      <Award className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#0d1b2a]">Nacional</p>
                      <p className="text-sm text-gray-600">Cobertura</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0d1b2a] mb-4">
              Contáctanos
            </h2>
            <p className="text-xl text-gray-600">
              Estamos aquí para atenderte y resolver todas tus dudas
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#1565c0]">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1565c0] to-[#1e88e5] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0d1b2a] mb-3">WhatsApp</h3>
                <p className="text-gray-600 mb-4">+57 311 531 8547</p>
                <a 
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center border border-[#1565c0] text-[#1565c0] hover:bg-[#1565c0] hover:text-white transition-all duration-300 px-4 py-2 rounded-md font-medium"
                >
                  Abrir WhatsApp
                </a>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#00c853]">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#00c853] to-[#43a047] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0d1b2a] mb-3">Email</h3>
                <p className="text-gray-600 mb-4">proteccapital@gmail.com</p>
                <Button 
                  onClick={() => window.location.href = 'mailto:proteccapital@gmail.com'}
                  variant="outline"
                  className="border-[#00c853] text-[#00c853] hover:bg-[#00c853] hover:text-white transition-all duration-300"
                >
                  Enviar Email
                </Button>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#1565c0]">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1565c0] to-[#00c853] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0d1b2a] mb-3">Ubicación</h3>
                <p className="text-gray-600 mb-4">Carrera 80 #13-130</p>
                <p className="text-sm text-gray-500">Cali, Colombia</p>
                <p className="text-sm text-[#00c853] font-medium mt-2">Cobertura Nacional</p>
              </CardContent>
            </Card>
          </div>

          {/* Forms Section */}
          <div className="max-w-4xl mx-auto">
            <Tabs defaultValue="quote" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-gray-100 p-1 rounded-xl">
                <TabsTrigger 
                  value="quote" 
                  className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1565c0] data-[state=active]:to-[#00c853] data-[state=active]:text-white transition-all duration-300 py-3 text-base font-semibold"
                >
                  Solicitar Cotización
                </TabsTrigger>
                <TabsTrigger 
                  value="contact"
                  className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1565c0] data-[state=active]:to-[#00c853] data-[state=active]:text-white transition-all duration-300 py-3 text-base font-semibold"
                >
                  Enviar Mensaje
                </TabsTrigger>
              </TabsList>
              <TabsContent value="quote">
                <QuoteForm />
              </TabsContent>
              <TabsContent value="contact">
                <ContactForm />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0d1b2a] text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <h3 className="text-2xl font-bold mb-2">
                  <span className="text-[#43a047]">Protec</span>
                  <span className="text-[#1565c0]">Capital</span>
                </h3>
                <p className="text-sm text-gray-400">Seguros & Capital</p>
              </div>
              <p className="text-gray-400 text-sm">
                Seguros y créditos con garantía real para proteger y hacer crecer tu patrimonio.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Servicios</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Seguros de Vida</li>
                <li>Seguros de Hogar</li>
                <li>Seguros de Autos</li>
                <li>Créditos Vehiculares</li>
                <li>Créditos Hipotecarios</li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Compañía</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><button onClick={() => scrollToSection('nosotros')}>Sobre Nosotros</button></li>
                <li><button onClick={() => scrollToSection('servicios')}>Servicios</button></li>
                <li><button onClick={() => scrollToSection('contacto')}>Contacto</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Síguenos</h4>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/proteccapital/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-all duration-300"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
              <p className="text-gray-400 text-sm mt-4">
                Carrera 80 #13-130
                <br />Cali, Colombia
                <br />Cobertura Nacional
              </p>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2026 ProtecCapital. Todos los derechos reservados. | Seguros & Capital</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 z-50 animate-bounce"
        aria-label="Contact via WhatsApp"
      >
        <Phone className="w-7 h-7 text-white" />
      </a>
    </div>
  );
};

export default Home;
